import { FC, Fragment, useCallback, useRef, useState } from "react";
import { Autocomplete as MuiAutocomplete } from "@material-ui/lab";
import { ButtonBase, createStyles, Dialog, FilledInput, Grid, makeStyles, Slide, Tooltip, Typography } from "@material-ui/core";
import { useDebouncedCallback } from "use-debounce";
import { searchService } from "../../store/services/search.service";
import type { ContactOrRealEstate } from "../../api/controllers/search";
import { House, Person, PermContactCalendar, SearchOutlined } from "@material-ui/icons";
import { parseAddress } from "../../utils/formatters";
import { match } from "ts-pattern";
import { useRouter } from "next/router";
import { create } from "superstruct";

const isJSON = (str: string) => {
    try {
        JSON.parse(str);
        return true;
    } catch (error) {
        return false;
    }
};

const useStyles = makeStyles(theme =>
    createStyles({
        dialogCard: {
            display: "hidden",
            position: "absolute",
            zIndex: 1000,
            background: "#ffffff30",
            backdropFilter: "blur(3px)",
            webkitBackdropFilter: "blur(3px)",
            borderLeft: `1px solid #ffffff30`,
            borderBottom: `1px solid #ffffff30`,
            borderRadius: "0 0 0 8px",
            top: 0,
            right: 0,
            alignItems: "center",
            justifyContent: "center",
            padding: theme.spacing(2),
            width: 380,
            margin: 0
        },
        dialog: {
            position: "absolute",
            top: 0,
            right: 0
        }
    })
);

export const Autocomplete: FC = () => {
    const [options, setOptions] = useState([]);
    const [inputValue, setInputValue] = useState("");
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const router = useRouter();
    const classes = useStyles();
    const searchRef = useRef(null);

    const fetchOptions = useDebouncedCallback(async (query: string): Promise<void> => {
        try {
            if (query === "" || isJSON(query)) {
                setOptions([]);
                return;
            }
            setLoading(true);
            const resp = await searchService.search(query);
            const { result } = resp.data;
            setOptions(result ?? []);
            setLoading(false);
        } catch (e) {}
    }, 500);

    const onInputChange = useCallback(
        (_e: any, value: string) => {
            setInputValue(value);
            // debounced fetching go brrrrrrrrrr
            fetchOptions(value);
        },
        [fetchOptions]
    );

    const onChange = useCallback(
        (e: React.ChangeEvent, value: ContactOrRealEstate) => {
            e.preventDefault();
            e.stopPropagation();
            // reset input value
            setInputValue("");

            if (!value) return;
            // go to value (this might change over time) -> Integration with rest of software
            match(value)
                .with({ type: "contact" }, value => {
                    // a contact was selected
                    const pathname =
                        value.variant === "networkPartner"
                            ? "network-partner"
                            : value.variant === "prospectiveBuyer"
                              ? "prospective-buyers"
                              : value.variant === "lead"
                                ? "/leads"
                                : "/contacts";

                    router.push(`/${pathname}/${value._id}`);
                })
                .with({ type: "real-estate" }, value => {
                    // route to a real-estate
                    // router.push({
                    //     pathname: "real-estate",
                    //     query: { defaultOpen: value._id }
                    // });
                    router.push(`/real-estate/${value._id}`);
                })
                .exhaustive();
        },
        [router]
    );

    const handleOpenSearch = () => setOpenDialog(true);
    const handleCloseSearch = () => setOpenDialog(false);

    return (
        <Fragment>
            <Dialog
                className={classes.dialog}
                PaperProps={{ className: classes.dialogCard }}
                open={openDialog}
                onClose={handleCloseSearch}
                maxWidth={false}
                TransitionComponent={Slide}
            >
                <MuiAutocomplete
                    id="combo-box-demo"
                    inputValue={inputValue}
                    onInputChange={onInputChange}
                    value={null}
                    onChange={onChange}
                    options={options}
                    getOptionLabel={(option: ContactOrRealEstate) => JSON.stringify(option)}
                    renderOption={(option: ContactOrRealEstate) => (
                        <Grid container wrap={"nowrap"} style={{ maxWidth: "100%" }}>
                            <Grid item>{option.type === "contact" ? option.variant === "lead" ? <Person /> : <PermContactCalendar /> : <House />}</Grid>
                            <Grid item xs>
                                <Typography style={{ fontSize: 12, fontWeight: 400 }}>
                                    {option.type === "contact" ? option.meta.name + "-" + parseAddress(option.meta?.address) : parseAddress(option.address)}
                                </Typography>
                            </Grid>
                        </Grid>
                    )}
                    style={{ width: "100%" }}
                    loading={loading}
                    loadingText={<Typography style={{ fontSize: 12, fontWeight: 400 }}>Lädt...</Typography>}
                    open={open}
                    onOpen={() => {
                        setOpen(true);
                    }}
                    onClose={() => {
                        setOpen(false);
                    }}
                    clearOnBlur
                    noOptionsText={<Typography style={{ fontSize: 12, fontWeight: 400 }}>Ich suche nach...</Typography>}
                    renderInput={params => (
                        <div ref={params.InputProps.ref}>
                            <FilledInput
                                {...params}
                                autoFocus
                                placeholder={"Immobilie oder Kontakt suchen..."}
                                style={{ color: "#ADB5BD", borderRadius: "4px" }}
                            />
                        </div>
                    )}
                />
            </Dialog>
            <Tooltip title={"Suchen Sie nach Immobilien oder Kontakten."}>
                <ButtonBase onClick={handleOpenSearch}>
                    <SearchOutlined style={{ width: 20, height: 20, marginRight: 4 }} />
                </ButtonBase>
            </Tooltip>
        </Fragment>
    );
};
